'use client';

import { useState, useCallback } from 'react';
import { motion } from 'motion/react';
import { useLocale } from './LocaleProvider';
import { dict, t } from '@/lib/i18n';

export function Newsletter() {
  const { locale } = useLocale();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus('error');
      return;
    }
    setStatus('success');
    setEmail('');
    try {
      await fetch('https://api.mysophia.tech/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          feature: 'newsletter',
          source: 'landing_newsletter',
          locale,
        }),
      });
    } catch {
      /* silent — UX already showed success */
    }
  }, [email, locale]);

  return (
    <section className="relative py-20">
      <div className="mx-auto max-w-xl px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-gold-soft font-semibold mb-4">
            <span className="w-1 h-1 rounded-full bg-gold-soft" />
            {t(dict.newsletter.eyebrow, locale)}
          </div>
          <h2 className="font-serif text-3xl text-fog-100 leading-tight mb-3">
            {t(dict.newsletter.heading, locale)}
          </h2>
          <p className="text-fog-500 text-sm mb-8">
            {t(dict.newsletter.body, locale)}
          </p>

          {status === 'success' ? (
            <div className="rounded-luxe bg-emerald-950/30 border border-emerald-500/20 p-5">
              <span className="text-emerald-400 text-2xl block mb-2">✓</span>
              <p className="text-emerald-300 text-sm font-medium">
                {t(dict.newsletter.success, locale)}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex gap-2 max-w-md mx-auto">
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setStatus('idle'); }}
                placeholder={t(dict.newsletter.placeholder, locale)}
                aria-label={t(dict.newsletter.placeholder, locale)}
                className="flex-1 px-4 py-3 rounded-pill bg-ink-700 border border-white/10 text-fog-100 text-sm placeholder:text-fog-700 focus:border-gold/40 focus:outline-none transition-[border-color,background-color] duration-150 ease-out"
              />
              <button
                type="submit"
                className="press-lift px-6 py-3 rounded-pill bg-cta-gradient text-white text-sm font-semibold shadow-glow transition-[background,transform,box-shadow] duration-200 ease-out hover:bg-cta-gradient-hover shrink-0"
              >
                {t(dict.newsletter.button, locale)}
              </button>
            </form>
          )}
          {status === 'error' && (
            <p className="text-red-400 text-xs mt-2">
              {t(dict.newsletter.error, locale)}
            </p>
          )}
        </motion.div>
      </div>
    </section>
  );
}
